import { useState } from 'react'

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'
import { useViewportWidth } from './ViewportWidth'

type Breakpoint = 'initial' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const breakpoints: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl']
const breakpointValues = [360, 640, 768, 1024, 1280]

function breakpointForWidth(width: number): Breakpoint {
  const activeIndex = breakpointValues.reduce(
    (acc, value, index) => (width >= value ? index : acc),
    -1,
  )
  return activeIndex >= 0 ? breakpoints[activeIndex] : 'initial'
}

/**
 * The active breakpoint, from the window's width, or from the width set by
 * the nearest `ViewportWidthProvider`, which Popcorn Kernel on the marketing
 * site sets for each product screen it renders.
 */
export function useBreakpoint(): Breakpoint {
  const viewportWidth = useViewportWidth()
  const [activeBreakpoint, setActiveBreakpoint] =
    useState<Breakpoint>('initial')

  useIsomorphicLayoutEffect(() => {
    // Create media query strings for each breakpoint
    const mediaQueries = breakpointValues.map(
      (value) => `(min-width: ${value}px)`,
    )

    // Function to determine active breakpoint
    const getActiveBreakpoint = () => {
      const activeIndex = mediaQueries.reduce((acc, query, index) => {
        if (window.matchMedia(query).matches) {
          return index
        }
        return acc
      }, -1)

      return activeIndex >= 0 ? breakpoints[activeIndex] : 'initial'
    }

    // Update active breakpoint
    const updateBreakpoint = () => {
      setActiveBreakpoint(getActiveBreakpoint())
    }

    // Set initial value
    updateBreakpoint()

    // Add listeners for each breakpoint
    const mediaListeners = mediaQueries.map((query) => {
      const mql = window.matchMedia(query)
      mql.addEventListener('change', updateBreakpoint)
      return mql
    })

    // Cleanup
    return () => {
      mediaListeners.forEach((mql) => {
        mql.removeEventListener('change', updateBreakpoint)
      })
    }
  }, [])

  if (viewportWidth !== null) {
    return breakpointForWidth(viewportWidth)
  }

  return activeBreakpoint
}
